import React, {useState, useEffect} from "react";
import {
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  Button,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {useLocation} from "react-router-dom";
import {useAuth0} from "@auth0/auth0-react";
import InfoIcon from "@mui/icons-material/Info";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import {useTranslation} from "react-i18next";
import {useStateContext} from "contexts/userContext";
import {client_url} from "constants/api";
import LangSwitcher from "./LangSwitcher";

export const Header = () => {
  const {isAuthenticated} = useAuth0();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [imageExists, setImageExists] = useState(false);
  const {t} = useTranslation();
  const {restaurant_id} = useStateContext();
  const theme = useTheme();

  // Извлекаем имя страницы из пути
  const namePage = location.pathname.slice(1);

  // Проверка доступности изображения
  const checkImageExists = (path) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = path;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  useEffect(() => {
    if (!namePage) {
      setImageExists(false);
      return;
    }

    const imagePath = `/images/instructions/${namePage}/${namePage}.png`;

    checkImageExists(imagePath).then((exists) => {
      setImageExists(exists);
      console.log(`Image exists for ${namePage}:`, exists);
    });
  }, [namePage]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Форматирование названия страницы
  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const link_to_menu = `${client_url}?restaurant_id=${restaurant_id}`;

  return (
    <>
      {isAuthenticated && (
        <Box
          sx={{
            width: "100%",
            bgcolor: "background.paper",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            position: "sticky",
            top: 0,
            zIndex: 1100,
            backdropFilter: "blur(10px)",
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: {xs: 2, md: 4},
              py: 2,
              maxWidth: "1600px",
              mx: "auto",
            }}
          >
            {/* Center - Page Title */}
            <Box sx={{
              position: "absolute",
              left: {xs: "60px", md: "50%"},
              transform: {xs: "none", md: "translateX(-50%)"},
              display: "flex",
              alignItems: "center",
              gap: 2
            }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: {xs: "1.25rem", md: "1.5rem"},
                }}
              >
                {!namePage ? t("Home Page") : t(capitalizeFirstLetter(namePage))}
              </Typography>

              {/* Help Icon */}
              {imageExists && (
                <IconButton
                  size="small"
                  onClick={handleClickOpen}
                  sx={{
                    color: "text.secondary",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: "primary.main",
                    },
                  }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {/* Spacer for layout */}
            <Box sx={{ flex: 1 }} />

            {/* Right Side - Menu Link & Language Switcher */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RestaurantMenuIcon />}
                endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                href={link_to_menu}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                {t("Menu")}
              </Button>

              <LangSwitcher />
            </Box>
          </Box>

          {/* Диалог с изображением */}
          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            PaperProps={{
              sx: {
                borderRadius: "16px",
              },
            }}
          >
            <DialogContent sx={{p: 0}}>
              <img
                src={`/images/instructions/${namePage}/${namePage}.png`}
                alt="instruction"
                style={{width: "100%", display: "block", borderRadius: "16px"}}
              />
            </DialogContent>
          </Dialog>
        </Box>
      )}
    </>
  );
};
